import React from 'react';
import { useConfigStore } from '@stores/configStore';
import { Database, AlertCircle, CheckCircle } from 'lucide-react';
import { OracleDatabaseForm } from './OracleDatabaseForm';
import type { DatabaseConfig, PostgreSQLConfig, OracleDatabaseConfig } from '../types/config';

interface DatabaseConfigFormProps {
  onTestConnection?: () => Promise<void>;
  testResult?: { success: boolean; message: string };
}

export const DatabaseConfigForm: React.FC<DatabaseConfigFormProps> = ({ onTestConnection, testResult }) => {
  const { config, updateDatabase } = useConfigStore();
  const db = config.database;

  const handleDatabaseTypeChange = (type: 'postgresql' | 'oracle') => {
    updateDatabase({ type });
  };

  const handlePostgreSQLUpdate = (updates: Partial<PostgreSQLConfig>) => {
    updateDatabase({
      postgresql: { ...db.postgresql, ...updates },
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-4">
        <Database className="w-5 h-5 text-blue-600" />
        <h3 className="text-lg font-semibold">Database Connection</h3>
      </div>

      {/* Database Type Selector */}
      <div className="border-b pb-4">
        <label className="block text-sm font-medium text-gray-700 mb-3">Database Type</label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              checked={db.type === 'postgresql'}
              onChange={() => handleDatabaseTypeChange('postgresql')}
              className="w-4 h-4 text-blue-600"
            />
            <span className="text-sm font-medium text-gray-700">PostgreSQL</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              checked={db.type === 'oracle'}
              onChange={() => handleDatabaseTypeChange('oracle')}
              className="w-4 h-4 text-blue-600"
            />
            <span className="text-sm font-medium text-gray-700">Oracle Database</span>
          </label>
        </div>
      </div>

      {/* PostgreSQL Configuration */}
      {db.type === 'postgresql' && db.postgresql && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Host</label>
              <input
                type="text"
                value={db.postgresql.host}
                onChange={(e) => handlePostgreSQLUpdate({ host: e.target.value })}
                placeholder="e.g., 103.191.208.235"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Port</label>
              <input
                type="number"
                value={db.postgresql.port}
                onChange={(e) => handlePostgreSQLUpdate({ port: parseInt(e.target.value) })}
                placeholder="5432"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Database Name</label>
              <input
                type="text"
                value={db.postgresql.database}
                onChange={(e) => handlePostgreSQLUpdate({ database: e.target.value })}
                placeholder="opusoa"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
              <input
                type="text"
                value={db.postgresql.username}
                onChange={(e) => handlePostgreSQLUpdate({ username: e.target.value })}
                placeholder="opusoa"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              type="password"
              value={db.postgresql.password}
              onChange={(e) => handlePostgreSQLUpdate({ password: e.target.value })}
              placeholder="••••••••"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pool Size</label>
              <input
                type="number"
                value={db.postgresql.poolSize}
                onChange={(e) => handlePostgreSQLUpdate({ poolSize: parseInt(e.target.value) })}
                min="1"
                max="100"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Connection Timeout (ms)</label>
              <input
                type="number"
                value={db.postgresql.connectionTimeout}
                onChange={(e) => handlePostgreSQLUpdate({ connectionTimeout: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Idle Timeout (ms)</label>
              <input
                type="number"
                value={db.postgresql.idleTimeout}
                onChange={(e) => handlePostgreSQLUpdate({ idleTimeout: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="pgssl"
              checked={db.postgresql.ssl}
              onChange={(e) => handlePostgreSQLUpdate({ ssl: e.target.checked })}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <label htmlFor="pgssl" className="text-sm font-medium text-gray-700">
              Use SSL Connection
            </label>
          </div>
        </div>
      )}

      {/* Oracle Configuration */}
      {db.type === 'oracle' && db.oracle && (
        <OracleDatabaseForm onTestConnection={onTestConnection} testResult={testResult} />
      )}

      {/* Test Connection Button (PostgreSQL only) */}
      {db.type === 'postgresql' && onTestConnection && (
        <button
          onClick={onTestConnection}
          className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
        >
          Test Connection
        </button>
      )}

      {/* Test Result (PostgreSQL only) */}
      {db.type === 'postgresql' && testResult && (
        <div className={`p-4 rounded-lg flex items-start gap-3 ${testResult.success ? 'bg-green-50' : 'bg-red-50'}`}>
          {testResult.success ? (
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          )}
          <p className={testResult.success ? 'text-green-800' : 'text-red-800'}>{testResult.message}</p>
        </div>
      )}
    </div>
  );
};
